import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import type { CreateUserRequest, OkResponse, UpdateUserRequest, User } from "../types";

// Admin-only (/api/users/*, role='admin' required — enforced server-side;
// the frontend also gates the /users route + nav item on isAdmin for UX).
export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    // GET /api/users returns { users: User[] } (same wrapper convention as
    // bot-instances' { instances: [...] }), not a bare array — was typed
    // and unwrapped as User[] directly here, so `data` held the whole
    // wrapper object; Users.tsx's `(users ?? []).map(...)` then threw at
    // render time as soon as the query resolved (real bug: reported as the
    // page being stuck on "Загрузка…" — the error crashed the render with
    // no error boundary to show anything else, freezing the last frame).
    queryFn: async () => (await api.get<{ users: User[] }>("/users")).users,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    // POST /api/users returns {ok, id}, not the created User object — the
    // return value isn't used here (onSuccess just invalidates the list),
    // so this was a silent type lie rather than a runtime bug, but worth
    // being honest about while already fixing the sibling mismatch above.
    mutationFn: (body: CreateUserRequest) => api.post<{ ok: boolean; id: number }>("/users", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateUserRequest & { id: number }) => api.patch<OkResponse>(`/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}
